/**
 * PayrollEmployeeProfile — Knit-style per-employee payroll profile, opened
 * from the pencil beside each employee name on the Payroll tab.
 * Layout mirrors Knit's employee page: header (photo, name, title, contact,
 * notes, Remove From Payroll) + tabs PERSONAL INFO / EMPLOYMENT /
 * COMPENSATION / DIRECT DEPOSIT / PTO / DOCUMENTS / PAYSTUBS.
 * Every dropdown carries Knit's exact option lists (knitData.js); text
 * inputs autosave (700 ms debounce) and selects save on change.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Plus, Trash2, Upload, User, X } from "lucide-react";
import {
  YES_NO, SEX_OPTS, SHIRT_OPTS, DEPENDENTS_OPTS, PROVINCES, COUNTRIES, BANKS,
  ACCOUNT_TYPES, FEDERAL_TD1_CODES, PROVINCIAL_TD1_CODES, T4_BOX45_CODES,
  EMPLOYEE_TYPES, EMPLOYEE_TYPE_OPTIONS, WAGE_PER_OPTIONS, PAYGROUPS,
  PAYMENT_FREQUENCIES, VACATION_PAYMENT_OPTIONS, WORK_LOCATIONS,
  PAY_RATE_LOCATIONS, WCB_LOCATIONS, DEPARTMENTS, PTO_STATUSES,
} from "./payroll/knitData";
import DismissEmployeeModal from "./payroll/DismissEmployeeModal";
import { AddRecurringIncomeModal, AddRecurringDeductionModal } from "./payroll/RecurringModals";
import DocumentsTab from "./payroll/DocumentsTab";
import RoeCreate from "./payroll/RoeCreate";
import KnitPaystubView from "./payroll/KnitPaystubView";
import ImportedStubCard from "./payroll/ImportedStubCard";
import { StubImportProgress } from "./payroll/StubImportProgress";

const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TABS = ["PERSONAL INFO", "EMPLOYMENT", "COMPENSATION", "DIRECT DEPOSIT", "PTO", "DOCUMENTS", "PAYSTUBS"];

/** Stored value ↔ Knit label maps. */
const EMP_TYPE_TO_LABEL = { full_time: "Full time", part_time: "Part time", intern: "Intern", contractor: "Contractor" };
const EMP_TYPE_TO_VALUE = Object.fromEntries(Object.entries(EMP_TYPE_TO_LABEL).map(([v, l]) => [l, v]));
const PAY_TYPE_TO_LABEL = { hourly: "Hourly", salary: "Salaried" };
const PAY_TYPE_TO_VALUE = { Hourly: "hourly", Salaried: "salary" };

const tenure = (hiredAt) => {
  if (!hiredAt) return "";
  const s = new Date(`${hiredAt}T12:00:00`);
  if (isNaN(s)) return "";
  const now = new Date();
  let months = (now.getFullYear() - s.getFullYear()) * 12 + (now.getMonth() - s.getMonth());
  let days = now.getDate() - s.getDate();
  if (days < 0) { months -= 1; const prev = new Date(now.getFullYear(), now.getMonth(), 0); days += prev.getDate(); }
  if (months < 0) return "";
  return `${months} months ${days} days`;
};

/** Debounced autosave text input. clearOnFocus wipes a masked display value
 *  (e.g. SIN "953******") so typing starts fresh; blurring without typing
 *  restores the masked value and saves nothing. */
const Field = ({ label, value, onSave, placeholder = "", mono = false, testid, readOnly = false, type = "text", clearOnFocus = false }) => {
  const [text, setText] = useState(value ?? "");
  const [flash, setFlash] = useState(false);
  const timer = useRef(null);
  const touched = useRef(false);
  useEffect(() => { setText(value ?? ""); }, [value]);
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <label className="block">
      <span className="mb-0.5 block font-mono text-[9px] uppercase tracking-widest text-white/40">{label}</span>
      <input
        type={type}
        value={text}
        readOnly={readOnly}
        placeholder={placeholder}
        data-testid={testid}
        onFocus={() => {
          touched.current = false;
          if (clearOnFocus && /[*•]/.test(text)) setText("");
        }}
        onBlur={() => {
          if (clearOnFocus && !touched.current) setText(value ?? "");
        }}
        onChange={(e) => {
          if (readOnly) return;
          touched.current = true;
          setText(e.target.value);
          clearTimeout(timer.current);
          timer.current = setTimeout(async () => {
            const ok = await onSave(e.target.value);
            if (ok) { setFlash(true); setTimeout(() => setFlash(false), 900); }
          }, 700);
        }}
        className={`h-8 w-full rounded-md border bg-white/[0.03] px-2 text-sm transition-colors ${
          flash ? "border-emerald-500/60" : "border-white/10"
        } ${readOnly ? "cursor-default text-white/50" : "text-white/90 focus:border-[#1a3fd1] focus:outline-none"} ${mono ? "font-mono" : ""}`}
      />
    </label>
  );
};

/** Select with Knit option lists. Options may be strings or {label, value}. */
const Sel = ({ label, value, onSave, options, testid, disabled = false, placeholder = "" }) => {
  const opts = useMemo(() => {
    const list = (options || []).map((o) => (typeof o === "string" ? { label: o, value: o } : o));
    if (value && !list.some((o) => o.value === value)) list.unshift({ label: String(value), value });
    return list;
  }, [options, value]);
  return (
    <label className="block">
      <span className="mb-0.5 block font-mono text-[9px] uppercase tracking-widest text-white/40">{label}</span>
      <select
        value={value ?? ""}
        disabled={disabled}
        data-testid={testid}
        onChange={(e) => onSave?.(e.target.value)}
        className={`h-8 w-full rounded-md border border-white/10 bg-white/[0.03] px-2 text-sm text-white/90 focus:border-[#1a3fd1] focus:outline-none disabled:opacity-50`}
      >
        {placeholder != null && <option value="">{placeholder}</option>}
        {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
};

/** Human label for one dated compensation-history entry. */
const histEntryLabel = (h) => {
  if (h.field === "hourly_rate") return `$${Number(h.value).toFixed(2)}/hr`;
  if (h.field === "weekly_salary") return `$${Number(h.value).toFixed(2)}/wk`;
  if (h.field === "overtime_exempt") return `OT exempt: ${h.value ? "Yes" : "No"}`;
  if (h.field === "vacation_rate") return h.value == null ? "Vacation: auto (ESA)" : `Vacation: ${Number(h.value)}%`;
  if (h.field === "vacation_payment_option") return `Vacation payout: ${h.value}`;
  return `${h.field}: ${h.value}`;
};

const SectionTitle = ({ children }) => (
  <p className="mb-3 mt-6 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">
    {children}
  </p>
);

const STATUS_OPTS = ["Active", "Inactive", "On-Hold", "Completed"];

export default function PayrollEmployeeProfile({ employeeId, onClose, onChanged }) {
  const [emp, setEmp] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);
  const [company, setCompany] = useState(null);
  const [tab, setTab] = useState("PERSONAL INFO");
  const [stubs, setStubs] = useState([]);
  const [timeOff, setTimeOff] = useState([]);
  const [stubYear, setStubYear] = useState("");
  const [viewStubId, setViewStubId] = useState(null);
  const [ptoFilters, setPtoFilters] = useState({ status: "", q: "", from: "", to: "" });
  const [dismissOpen, setDismissOpen] = useState(false);
  const [addIncomeOpen, setAddIncomeOpen] = useState(false);
  const [addDeductionOpen, setAddDeductionOpen] = useState(false);
  const [roe, setRoe] = useState(null);        // null | "new" | roe draft object
  const [hireEdit, setHireEdit] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // {verified, reason, changed_at} | null

  const load = useCallback(async () => {
    try {
      const [e, ps, to, all, co] = await Promise.all([
        api.get(`/employees/${employeeId}`),
        api.get(`/payroll/paystubs`, { params: { employee_id: employeeId } }),
        api.get(`/time-off`).catch(() => ({ data: [] })),
        api.get(`/employees`).catch(() => ({ data: [] })),
        api.get(`/settings/company-profile`).catch(() => ({ data: null })),
      ]);
      setEmp(e.data);
      setStubs(ps.data || []);
      setTimeOff((to.data || []).filter((r) => r.employee_id === employeeId));
      setAllEmployees(all.data || []);
      setCompany(co.data || null);
      // Self-entered emails stay unverified until office staff confirm them
      api.get(`/employees/${employeeId}/email-status`).then((r) => setEmailStatus(r.data)).catch(() => setEmailStatus(null));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load the employee");
      onClose();
    }
  }, [employeeId, onClose]);
  useEffect(() => { load(); }, [load]);

  /* Browser/PWA Back integration: the overlay pushes a history entry on open
     (and one more when CREATE ROE opens) so the device Back button closes the
     overlay instead of sending the dashboard back to the previous ?tab=. */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const rootPushed = useRef(false);
  const roePushed = useRef(false);

  /** Close the whole overlay, unwinding the pushed history entry. */
  const closeSelf = useCallback(() => {
    if (rootPushed.current) {
      rootPushed.current = false;
      window.history.back(); // popstate handler below calls onClose
    } else {
      onCloseRef.current();
    }
  }, []);
  /** Back out of CREATE ROE into the profile. */
  const closeRoe = useCallback(() => {
    if (roePushed.current) {
      roePushed.current = false;
      window.history.back(); // popstate handler below clears roe
    } else {
      setRoe(null);
    }
  }, []);

  useEffect(() => {
    window.history.pushState({ empProfile: employeeId }, "");
    rootPushed.current = true;
    const onPop = () => {
      if (roePushed.current) {
        roePushed.current = false;
        setRoe(null);
        return;
      }
      rootPushed.current = false;
      onCloseRef.current();
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [employeeId]);

  useEffect(() => {
    if (roe && !roePushed.current) {
      window.history.pushState({ empProfile: "roe" }, "");
      roePushed.current = true;
    }
  }, [roe]);

  /** save one employee field; returns success for the flash. */
  const save = useCallback((key) => async (val) => {
    try {
      const { data } = await api.put(`/employees/${employeeId}`, { [key]: val });
      setEmp(data);
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
      return false;
    }
  }, [employeeId]);

  /** save several fields in one PUT. */
  const saveMulti = useCallback(async (obj) => {
    try {
      const { data } = await api.put(`/employees/${employeeId}`, obj);
      setEmp(data);
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
      return false;
    }
  }, [employeeId]);

  /** merge-save one key inside tax_options. */
  const saveTax = useCallback((key) => async (val) => {
    const merged = { ...(emp?.tax_options || {}), [key]: val };
    return saveMulti({ tax_options: merged });
  }, [emp, saveMulti]);

  /** Knit Wage Per/Wage → our pay_type + rate/salary fields. A changed rate
   * is saved with the chosen effective date so earlier payroll weeks keep
   * the old rate (backend records it in rate_history). No default date —
   * the admin must pick when the change applies from. */
  const [wageEffective, setWageEffective] = useState("");
  const [wageRefuseNonce, setWageRefuseNonce] = useState(0); // remounts the wage Field so a refused save snaps back
  const saveWage = useCallback(async (wagePer, wageAmt) => {
    const w = parseFloat(wageAmt) || 0;
    const payload = { wage_per: wagePer, wage_amount: w };
    if (wagePer === "Hour") {
      payload.pay_type = "hourly";
      payload.hourly_rate = w;
    } else {
      payload.pay_type = "salary";
      payload.weekly_salary = wagePer === "Week" ? w
        : wagePer === "Month" ? Math.round((w * 12) / 52 * 100) / 100
        : Math.round((w / 52) * 100) / 100;
    }
    const newRate = wagePer === "Hour" ? w : payload.weekly_salary;
    const curRate = wagePer === "Hour" ? Number(emp?.hourly_rate || 0) : Number(emp?.weekly_salary || 0);
    if (Math.abs(newRate - curRate) > 0.0001) {
      if (!wageEffective) {
        toast.error("Pick the date this rate applies from first");
        setWageRefuseNonce((n) => n + 1); // reset the wage input to the saved rate
        return false;
      }
      payload.rate_effective_date = wageEffective;
    }
    const ok = await saveMulti(payload);
    if (ok && payload.rate_effective_date) {
      toast.success(`New rate applies from ${payload.rate_effective_date} — earlier weeks keep the old rate`);
    }
    return ok;
  }, [saveMulti, emp, wageEffective]);

  /* Vacation pay rate % / payment option changes are also dated historical
   * compensation: intercept the save, ask when it applies, then PUT with
   * rate_effective_date so past payroll periods keep the old setting. */
  const [vacEff, setVacEff] = useState(null); // { key, value, label }
  const [vacEffDate, setVacEffDate] = useState("");
  const [vacEffBusy, setVacEffBusy] = useState(false);
  const [vacCancelNonce, setVacCancelNonce] = useState(0); // remounts the rate Field so a cancelled edit snaps back
  const cancelVacEffective = () => { setVacEff(null); setVacCancelNonce((n) => n + 1); };

  /* Imported Knit pay stubs — PDFs scanned into the employee's yearly pay
   * stats (home tab card). Each import is labelled with the pay date printed
   * in the PDF. */
  const [importedStubs, setImportedStubs] = useState([]);
  const [importingStub, setImportingStub] = useState(false);
  const [viewImport, setViewImport] = useState(null); // import_id → readable stub card
  const [importProgress, setImportProgress] = useState(null); // {done,total,pct,current}
  // Year check: which Mon–Sun pay periods have NO stub at all (blank $0
  // stubs count as present), plus the zip export of that year's originals.
  const [checkYear, setCheckYear] = useState(() => new Date().getFullYear());
  const [yearCheck, setYearCheck] = useState(null);
  const [showMissing, setShowMissing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const loadYearCheck = useCallback(async (yr) => {
    if (!emp?.employee_id) return;
    try {
      const r = await api.get(`/payroll/year-report/${emp.employee_id}`, { params: { year: yr } });
      setYearCheck(r.data);
    } catch { setYearCheck(null); }
  }, [emp?.employee_id]);
  useEffect(() => { loadYearCheck(checkYear); }, [loadYearCheck, checkYear]);
  const exportYearZip = async () => {
    setExporting(true);
    try {
      const r = await api.get(`/payroll/imported-paystubs/${emp.employee_id}/export`, { params: { year: checkYear }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(emp.name || "employee").replace(/[^\w\- ]+/g, "_").replace(/\s+/g, "_")}_paystubs_${checkYear}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      const d = e?.response?.data;
      let msg = `No imported stubs for ${checkYear}`;
      if (d instanceof Blob) { try { msg = JSON.parse(await d.text()).detail || msg; } catch { /* keep default */ } }
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };
  const loadImportedStubs = useCallback(async () => {
    if (!emp?.employee_id) return;
    try {
      const r = await api.get(`/payroll/imported-paystubs/${emp.employee_id}`);
      setImportedStubs(r.data.stubs || []);
    } catch { /* non-fatal */ }
  }, [emp?.employee_id]);
  useEffect(() => { loadImportedStubs(); }, [loadImportedStubs]);
  const importPaystub = async (ev) => {
    const files = Array.from(ev.target.files || []);
    ev.target.value = "";
    if (!files.length) return;
    setImportingStub(true);
    let done = 0;
    const tally = { imported: 0, duplicate: 0, blank: 0, failed: 0 };
    setImportProgress({ done, total: files.length, pct: 0, current: [files[0].name] });
    try {
      for (const f of files) {
        setImportProgress({ done, total: files.length, pct: Math.round((done / files.length) * 100), current: [f.name] });
        try {
          const fd = new FormData();
          fd.append("file", f);
          const onUploadProgress = (p) => {
            if (!p.total) return;
            setImportProgress({ done, total: files.length, pct: Math.min(99, Math.round(((done + (p.loaded / p.total) * 0.8) / files.length) * 100)), current: [f.name] });
          };
          let r;
          try {
            r = await api.post(`/payroll/imported-paystubs/${emp.employee_id}`, fd, { onUploadProgress });
          } catch (e1) {
            // 409 = the name printed inside the stub doesn't match this employee —
            // warn and only import on explicit confirmation.
            const d = e1?.response?.data?.detail;
            if (e1?.response?.status === 409 && d?.message === "name_mismatch") {
              const ok = window.confirm(
                `${f.name}: the name inside is "${d.stub_name}" but you're uploading it to ${d.employee_name}.\n\nImport it anyway?`
              );
              if (!ok) { tally.failed += 1; done += 1; continue; }
              const fd2 = new FormData();
              fd2.append("file", f);
              r = await api.post(`/payroll/imported-paystubs/${emp.employee_id}?confirm_mismatch=true`, fd2, { onUploadProgress });
            } else {
              throw e1;
            }
          }
          if (r.data.duplicate) tally.duplicate += 1;
          else { tally.imported += 1; if (r.data.blank) tally.blank += 1; }
          if (files.length === 1) {
            if (r.data.duplicate) toast.info(`${r.data.label} is already on file for this employee — not added again`);
            else if (r.data.blank) toast.warning(`Scanned ${r.data.label} — blank $0 stub, recorded but adds nothing to the totals`);
            else toast.success(`Scanned ${r.data.label} — gross $${Number(r.data.gross || 0).toFixed(2)}, net $${Number(r.data.net || 0).toFixed(2)}`);
          }
        } catch (e) {
          tally.failed += 1;
          toast.error(`${f.name}: ${e?.response?.data?.detail || "could not scan — is it a pay stub (PDF or CSV)?"}`);
        }
        done += 1;
      }
      setImportProgress({ done, total: files.length, pct: 100, current: [] });
      if (files.length > 1) {
        toast.success(`Imported ${tally.imported} of ${files.length}${tally.duplicate ? ` · ${tally.duplicate} already on file` : ""}${tally.blank ? ` · ${tally.blank} blank $0` : ""}${tally.failed ? ` · ${tally.failed} failed` : ""}`);
      }
      await loadImportedStubs();
      await loadYearCheck(checkYear);
    } finally {
      setImportingStub(false);
      setTimeout(() => setImportProgress(null), 1200);
    }
  };
  const deleteImportedStub = async (s) => {
    if (!window.confirm(`Remove "${s.label}"? Its amounts stop counting toward the yearly pay stats.`)) return;
    try {
      await api.delete(`/payroll/imported-paystubs/${s.import_id}`);
      setImportedStubs((xs) => xs.filter((x) => x.import_id !== s.import_id));
      toast.success("Imported pay stub removed");
    } catch {
      toast.error("Could not delete the import");
    }
  };
  const askVacEffective = (key, value, label) => {
    setVacEffDate("");
    setVacEff({ key, value, label });
  };
  const confirmVacEffective = async () => {
    if (!vacEffDate) { toast.error("Pick the date this change applies from"); return; }
    setVacEffBusy(true);
    const ok = await saveMulti({ [vacEff.key]: vacEff.value, rate_effective_date: vacEffDate });
    setVacEffBusy(false);
    if (ok) {
      toast.success(`${vacEff.label} applies from ${vacEffDate} — earlier weeks keep the old setting`);
      setVacEff(null);
    }
  };

  /** Dated compensation history: add a past/future rate with the date it
   * commenced, or remove an entry. Payroll resolves each week to the entry
   * in effect then (entered date → next change date, else → today). */
  const [addingRate, setAddingRate] = useState(false);
  const [newRate, setNewRate] = useState(() => ({ per: "Hour", amount: "", date: new Date().toISOString().slice(0, 10) }));
  const [savingRate, setSavingRate] = useState(false);
  const addRateEntry = async () => {
    const amt = parseFloat(newRate.amount);
    if (!amt || amt <= 0) { toast.error("Enter the pay rate amount"); return; }
    if (!newRate.date) { toast.error("Pick the date this rate commenced"); return; }
    setSavingRate(true);
    try {
      const { data } = await api.post(`/employees/${employeeId}/rate-history`, {
        wage_per: newRate.per, wage_amount: amt, effective_date: newRate.date,
      });
      setEmp(data);
      setAddingRate(false);
      setNewRate({ per: "Hour", amount: "", date: new Date().toISOString().slice(0, 10) });
      toast.success(`Rate applies from ${newRate.date} until the next change`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add the rate entry");
    } finally {
      setSavingRate(false);
    }
  };
  const deleteRateEntry = async (h) => {
    try {
      const { data } = await api.delete(`/employees/${employeeId}/rate-history`, {
        params: { field: h.field, effective_date: h.effective_date },
      });
      setEmp(data);
      toast.success("Rate entry removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove the entry");
    }
  };

  const addRecurringIncome = async (item) => {
    const list = [...(emp.recurring_incomes || []), { income_id: `ri_${Date.now()}`, ...item }];
    if (await saveMulti({ recurring_incomes: list })) {
      setAddIncomeOpen(false);
      toast.success(`${item.type} added — applies after the current payroll concludes`);
    }
  };
  const removeRecurringIncome = async (id) => {
    await saveMulti({ recurring_incomes: (emp.recurring_incomes || []).filter((x) => x.income_id !== id) });
    toast.success("Recurring income removed");
  };
  const addRecurringDeduction = async (item) => {
    const list = [...(emp.recurring_deductions || []), { deduction_id: `rd_${Date.now()}`, ...item }];
    if (await saveMulti({ recurring_deductions: list })) {
      setAddDeductionOpen(false);
      toast.success(`${item.type} deduction added`);
    }
  };
  const removeRecurringDeduction = async (id) => {
    await saveMulti({ recurring_deductions: (emp.recurring_deductions || []).filter((x) => x.deduction_id !== id) });
    toast.success("Deduction removed");
  };

  const deleteBankAccount = async () => {
    if (await saveMulti({ bank_institution: "", bank_transit: "", bank_account: "", bank_account_type: "Checking" })) {
      toast.success("Bank account deleted");
    }
  };

  /** Open the employee's signed vacation-waiver PDF (admin view). */
  const viewWaiver = async () => {
    try {
      const res = await api.get(`/employees/${employeeId}/vacation-waiver/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("No signed waiver on file for this employee");
    }
  };

  /* --- AI import from a Knit screenshot (image is scanned in memory and
        discarded server-side — never stored) --- */
  const importInput = useRef(null);
  const importSection = useRef("");
  const [scanning, setScanning] = useState("");
  const pickImport = (section) => {
    importSection.current = section;
    importInput.current?.click();
  };
  const onImportFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const section = importSection.current;
    setScanning(section || "auto");
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/employees/${employeeId}/import-scan`, fd, {
        params: { section: section || "auto" },
        timeout: 180000,
      });
      const n = Object.keys(data.applied || {}).length + (data.sensitive_applied || []).length;
      if (n) toast.success(`Imported ${n} field${n === 1 ? "" : "s"} from ${files.length} screenshot${files.length === 1 ? "" : "s"}`);
      (data.results || []).forEach((r) => {
        if (r.section === "unknown") toast.warning(`${r.file}: couldn't tell which page this is — skipped`);
        else toast.info(`${r.file} → ${r.section}: ${r.fields.length} field${r.fields.length === 1 ? "" : "s"}`);
      });
      if (data.note) toast.info(data.note);
      if ((data.skipped || []).length) toast.info(`Skipped: ${data.skipped.join(", ")}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read that screenshot");
    } finally {
      setScanning("");
    }
  };
  const ImportBtn = ({ section }) => (
    <button onClick={() => pickImport(section)} disabled={!!scanning} data-testid={`import-${section}-btn`}
      title="Upload a screenshot of this payroll page — AI fills the matching fields (image is deleted right after)"
      className="mb-3 mt-6 inline-flex items-center gap-1 rounded-md border border-white/15 px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest text-white/60 transition-colors hover:border-[#1a3fd1] hover:text-white disabled:opacity-50">
      {scanning === section ? "Scanning…" : (<><Upload className="h-3 w-3" /> Import</>)}
    </button>
  );

  const stubYears = useMemo(
    () => [...new Set(stubs.map((s) => (s.created_at || "").slice(0, 4)).filter(Boolean))].sort().reverse(),
    [stubs]
  );
  const shownStubs = useMemo(
    () => (stubYear ? stubs.filter((s) => (s.created_at || "").startsWith(stubYear)) : stubs),
    [stubs, stubYear]
  );
  const filteredTimeOff = useMemo(() => timeOff.filter((r) =>
    (!ptoFilters.status || (r.status || "").toLowerCase() === ptoFilters.status.toLowerCase()) &&
    (!ptoFilters.q || (r.reason || "").toLowerCase().includes(ptoFilters.q.toLowerCase())) &&
    (!ptoFilters.from || (r.date_from || "") >= ptoFilters.from) &&
    (!ptoFilters.to || (r.date_from || "") <= ptoFilters.to)
  ), [timeOff, ptoFilters]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (viewStubId) setViewStubId(null);
        else if (roe) closeRoe();
        else if (dismissOpen) setDismissOpen(false);
        else closeSelf();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeSelf, closeRoe, viewStubId, roe, dismissOpen]);

  if (!emp) {
    return (
      <div className="flex min-h-[300px] items-center justify-center" data-testid="payroll-profile-overlay">
        <p className="font-mono text-xs uppercase tracking-widest text-white/40">Loading employee…</p>
      </div>
    );
  }

  const nameParts = (emp.name || "").trim().split(/\s+/);
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";
  const isOwner = emp.linked_role === "owner";
  const tx = emp.tax_options || {};
  const wagePer = emp.wage_per || (emp.pay_type === "salary" ? "Week" : "Hour");
  const wageAmt = emp.wage_amount ?? (emp.pay_type === "salary" ? emp.weekly_salary : emp.hourly_rate) ?? "";
  const reportToOpts = allEmployees.filter((e) => e.employee_id !== emp.employee_id).map((e) => e.name);
  // "Office" work location resolves its address from the company profile's
  // primary location (Settings → Company → Locations).
  const primaryLoc = (company?.locations || []).find((l) => l.primary) || (company?.locations || [])[0];
  const officeAddress = primaryLoc
    ? [primaryLoc.address, primaryLoc.city, primaryLoc.province, primaryLoc.postal].filter(Boolean).join(", ")
    : "";
  const workLocationDisplay = emp.work_location === "Office" && officeAddress ? officeAddress : (emp.work_location || "—");

  return (
    <div className="flex min-h-full flex-col text-white" data-testid="payroll-profile-overlay">
      {/* window chrome — sticky so it stays pinned under the app tab strip */}
      <div className="sticky top-0 z-40 flex h-12 shrink-0 items-center gap-3 border-b border-white/10 bg-[#0a0a0a] px-4">
        <button onClick={roe ? closeRoe : closeSelf} data-testid="payroll-profile-back"
          className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white/70 hover:border-white/30 hover:text-white">
          <ArrowLeft className="h-3.5 w-3.5" /> {roe ? "Profile" : "Payroll"}
        </button>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
          {roe ? "Create ROE" : "Employee payroll profile"}
        </p>
        <button onClick={closeSelf} aria-label="Close" className="ml-auto rounded-md p-1.5 text-white/50 hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></button>
      </div>

      {/* tab strip (hidden inside CREATE ROE) */}
      {!roe && (
        <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-white/10 px-4" role="tablist">
          {TABS.map((t) => (
            <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
              data-testid={`profile-tab-${t.toLowerCase().replace(/\s/g, "-")}`}
              className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${tab === t ? "border-[#f97316] text-white" : "border-transparent text-white/40 hover:text-white/70"}`}>
              {t}
            </button>
          ))}
        </div>
      )}

      <div className="flex-1">
        <div className="mx-auto w-full max-w-[1100px] px-4 pb-16 pt-5">
          {roe ? (
            <RoeCreate
              employee={emp}
              roe={roe === "new" ? null : roe}
              onBack={closeRoe}
              onSaved={() => { closeRoe(); load(); }}
            />
          ) : (
          <>
          {/* Knit header block — stacks on phones: photo+name row, then
              contact info, then department/location/hire meta (desktop keeps
              the three-part Knit layout). */}
          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:gap-5" data-testid="profile-header">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-white/5 sm:h-20 sm:w-20">
                {emp.photo
                  ? <img src={emp.photo} alt={emp.name} className="h-full w-full object-cover" />
                  : <User className="h-9 w-9 text-white/20" />}
              </div>
              <div className="min-w-0 sm:hidden">
                <h2 className="break-words text-lg font-bold text-white">{emp.name}</h2>
                <p className="text-xs text-white/60">{emp.job_title || emp.trade || "—"}</p>
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="hidden text-lg font-bold text-white sm:block" data-testid="profile-name">{emp.name}</h2>
              <p className="hidden text-xs text-white/60 sm:block">{emp.job_title || emp.trade || "—"}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 sm:mt-0.5">
                Department: {emp.department || emp.trade || "—"} · Office Location: <span data-testid="profile-work-location-display" className="normal-case">{workLocationDisplay}</span>
              </p>
              <p className="mt-0.5 flex flex-wrap items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-white/40">
                Employed since {emp.hired_at || "—"}{emp.hired_at ? ` (${tenure(emp.hired_at)})` : ""}
                <button onClick={() => setHireEdit((v) => !v)} aria-label="Update hire date" data-testid="hire-date-pencil"
                  className="rounded p-0.5 text-[#8fa4f0] hover:bg-white/10 hover:text-white">
                  <Pencil className="h-3 w-3" />
                </button>
                {hireEdit && (
                  <input type="date" defaultValue={emp.hired_at || ""} data-testid="hire-date-input"
                    onChange={async (e) => { if (await save("hired_at")(e.target.value)) setHireEdit(false); }}
                    className="h-6 rounded border border-white/20 bg-white/[0.05] px-1 font-mono text-[10px] text-white focus:border-[#1a3fd1] focus:outline-none" />
                )}
              </p>
            </div>
            <div className="sm:text-right">
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Contact information</p>
              <p className="break-all text-xs text-white/70" data-testid="profile-email">{emp.email || "—"}</p>
              {emailStatus && emp.email && !emailStatus.verified && (
                <div className="mt-1 flex flex-wrap items-center gap-2 sm:justify-end" data-testid="profile-email-unverified">
                  <span className="rounded border border-amber-400/40 bg-amber-400/10 px-1.5 py-0.5 text-[10px] text-amber-200"
                    title="Entered by the employee. Not used for e-transfers or exit documents until verified.">
                    Unverified — entered by employee
                  </span>
                  <button type="button" data-testid="profile-email-verify"
                    className="rounded border border-white/20 px-1.5 py-0.5 text-[10px] text-white/80 hover:bg-white/[0.06]"
                    onClick={async () => {
                      if (!window.confirm(`Confirm ${emp.email} belongs to ${emp.name || "this employee"}? Check it with them directly (phone or in person), not by replying to that address.`)) return;
                      try {
                        const r = await api.post(`/employees/${emp.employee_id}/email/verify`, { email: emp.email });
                        setEmailStatus(r.data);
                        toast.success("Email verified");
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Could not verify the email");
                      }
                    }}>
                    Verify
                  </button>
                </div>
              )}
              <p className="text-xs text-white/70">{emp.phone || ""}</p>
            </div>
          </div>

          {/* notes + remove */}
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div className="min-w-[280px] flex-1">
              <p className="mb-0.5 font-mono text-[9px] uppercase tracking-widest text-white/40">Notes</p>
              <Field value={emp.notes || ""} onSave={save("notes")} testid="profile-notes"
                placeholder="Attach a note that is only viewable by the direct manager and admin" />
            </div>
            <button onClick={() => setDismissOpen(true)} data-testid="profile-remove-from-payroll"
              className="rounded-md bg-red-700 px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-widest text-white hover:bg-red-600">
              Remove From Payroll
            </button>
          </div>

          {/* ============================ PERSONAL INFO ============================ */}
          {tab === "PERSONAL INFO" && (
            <div data-testid="profile-personal-info">
              <div className="flex items-start justify-between gap-2">
                <SectionTitle>General Information</SectionTitle>
                <ImportBtn section="personal" />
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Field label="First name" value={firstName} onSave={async (v) => save("name")([v, emp.middle_name, lastName].filter(Boolean).join(" "))} testid="profile-first-name" />
                <Field label="Middle name" value={emp.middle_name || ""} onSave={async (v) => save("middle_name")(v)} testid="profile-middle-name" />
                <Field label="Last name" value={lastName} onSave={async (v) => save("name")([firstName, emp.middle_name, v].filter(Boolean).join(" "))} testid="profile-last-name" />
                <Field label="Phone number" value={emp.phone || ""} onSave={save("phone")} testid="profile-phone" />
                <Field label="Date of birth" value={emp.date_of_birth || ""} onSave={save("date_of_birth")} type="date" testid="profile-dob" />
                <Field label="Social Insurance Number" value={emp.sin || ""} onSave={save("sin")} testid="profile-sin" mono clearOnFocus placeholder="9 digits" />
                <Sel label="Dependents" value={emp.dependants || ""} onSave={save("dependants")} options={DEPENDENTS_OPTS} placeholder="" testid="profile-dependants" />
                <Sel label="Shirt size" value={emp.shirt_size || ""} onSave={save("shirt_size")} options={SHIRT_OPTS} placeholder="" testid="profile-shirt-size" />
                <Sel label="Sex" value={emp.sex || ""} onSave={save("sex")} options={SEX_OPTS} placeholder="" testid="profile-sex" />
                <div className="col-span-2 md:col-span-3">
                  <Field label="Dietary restrictions" value={emp.allergies || ""} onSave={save("allergies")} testid="profile-dietary" />
                </div>
              </div>

              <SectionTitle>Home Address</SectionTitle>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Sel label="Country" value={emp.address_country || "Canada"} onSave={save("address_country")} options={COUNTRIES} placeholder="" testid="profile-country" />
                <Sel label="Province/State" value={emp.address_province || "Ontario"} onSave={save("address_province")} options={PROVINCES} placeholder="" testid="profile-province" />
                <div className="col-span-2"><Field label="Address line 1" value={emp.address_street || ""} onSave={save("address_street")} testid="profile-address-1" /></div>
                <div className="col-span-2"><Field label="Address line 2" value={emp.address_line2 || ""} onSave={save("address_line2")} testid="profile-address-2" /></div>
                <Field label="City" value={emp.address_city || ""} onSave={save("address_city")} testid="profile-city" />
                <Field label="Postal/Zip Code" value={emp.address_postal || ""} onSave={save("address_postal")} testid="profile-postal" mono />
              </div>

              <SectionTitle>Emergency Contacts</SectionTitle>
              {[
                { n: 1, name: "emergency_contact_name", rel: "emergency_contact_relationship", ph: "emergency_contact_phone" },
                { n: 2, name: "emergency_contact_2_name", rel: "emergency_contact_2_relationship", ph: "emergency_contact_2_phone" },
              ].map(({ n, name, rel, ph }) => (
                <div key={n} className="mb-2 grid grid-cols-1 gap-3 md:grid-cols-3">
                  <Field label="Contact name" value={emp[name] || ""} onSave={save(name)} testid={`profile-emergency-${n}-name`} />
                  <Field label="Relationship" value={emp[rel] || ""} onSave={save(rel)} testid={`profile-emergency-${n}-relationship`} />
                  <Field label="Phone" value={emp[ph] || ""} onSave={save(ph)} testid={`profile-emergency-${n}-phone`} />
                </div>
              ))}
            </div>
          )}

          {/* ============================ EMPLOYMENT ============================ */}
          {tab === "EMPLOYMENT" && (
            <div data-testid="profile-employment">
              <div className="flex items-start justify-between gap-2">
                <SectionTitle>Job Details</SectionTitle>
                <ImportBtn section="employment" />
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                <Field label="Job title" value={emp.job_title || ""} onSave={save("job_title")} testid="profile-job-title" />
                <Field label="Employee number" value={emp.employee_number || ""} onSave={save("employee_number")} testid="profile-employee-number" />
                <div>
                  <Sel label="Work location" value={emp.work_location || ""} onSave={save("work_location")} options={WORK_LOCATIONS} placeholder="Please select" testid="profile-work-location" />
                  {emp.work_location === "Office" && officeAddress && (
                    <p className="mt-1 text-[11px] text-white/50" data-testid="profile-office-address">{officeAddress}</p>
                  )}
                </div>
                <Sel label="WCB location" value={emp.wcb_location || "Same as work location"} onSave={save("wcb_location")} options={WCB_LOCATIONS} placeholder={null} testid="profile-wcb-location" />
                <Sel label="Department" value={emp.department || ""} onSave={save("department")} options={DEPARTMENTS} placeholder="Please select" testid="profile-department" />
                <Field label="Hire date" value={emp.hired_at || ""} onSave={save("hired_at")} type="date" testid="profile-hired-at" />
                <Sel label="Report to" value={emp.report_to || ""} onSave={save("report_to")} options={reportToOpts} placeholder="Please select" testid="profile-report-to" />
                <Sel label="Status" value={emp.status || "Active"} onSave={save("status")} options={STATUS_OPTS} placeholder={null} testid="profile-status" />
                <Field label="Current position hire date" value={emp.position_hire_date || emp.hired_at || ""} onSave={save("position_hire_date")} type="date" testid="profile-position-hire-date" />
                <Sel label="Employment type" value={emp.employment_type || "full_time"}
                  onSave={async (v) => save("employment_type")(v)}
                  options={EMPLOYEE_TYPES.map((l) => ({ label: l, value: EMP_TYPE_TO_VALUE[l] }))}
                  placeholder={null} testid="profile-employment-type" />
              </div>
              {isOwner && (
                <p className="mt-4 inline-block rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-amber-300" data-testid="profile-owner-exempt-note">
                  Vacation exempt — owner role
                </p>
              )}
            </div>
          )}

          {/* ============================ COMPENSATION ============================ */}
          {tab === "COMPENSATION" && (
            <div data-testid="profile-compensation">
              <div className="flex items-start justify-between gap-2">
                <SectionTitle>Compensation</SectionTitle>
                <ImportBtn section="compensation" />
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Sel label="Paygroup" value={emp.paygroup || "Weekly"} onSave={save("paygroup")} options={PAYGROUPS} placeholder={null} testid="profile-paygroup" />
                <Sel label="Payment frequency" value={emp.payment_frequency || "Weekly"} onSave={save("payment_frequency")} options={PAYMENT_FREQUENCIES} placeholder={null} testid="profile-pay-frequency" />
                <Sel label="Employee type" value={emp.employment_type || "full_time"}
                  onSave={async (v) => save("employment_type")(v)}
                  options={EMPLOYEE_TYPES.map((l) => ({ label: l, value: EMP_TYPE_TO_VALUE[l] }))}
                  placeholder={null} testid="profile-employee-type" />
                <Sel label="Employee type option" value={emp.pay_type || "hourly"}
                  onSave={async (v) => save("pay_type")(v)}
                  options={EMPLOYEE_TYPE_OPTIONS.map((l) => ({ label: l, value: PAY_TYPE_TO_VALUE[l] }))}
                  placeholder={null} testid="profile-pay-type" />
                <Field key={`vac-rate-${vacCancelNonce}`} label="Vacation pay rate (%)" value={emp.vacation_rate ?? ""}
                  onSave={async (v) => {
                    const nv = v === "" ? null : parseFloat(v) || 0;
                    if ((nv ?? null) === (emp.vacation_rate ?? null)) return;
                    askVacEffective("vacation_rate", nv, "Vacation pay rate");
                  }}
                  placeholder="auto by tenure" testid="profile-vacation-rate" mono readOnly={isOwner} />
                <Sel label="Payment option" value={emp.vacation_payment_option || "Accrue until payout"}
                  onSave={(v) => {
                    if (v === (emp.vacation_payment_option || "Accrue until payout")) return;
                    askVacEffective("vacation_payment_option", v, "Vacation payment option");
                  }}
                  options={VACATION_PAYMENT_OPTIONS} placeholder="Please select" testid="profile-vacation-payment-option" />
                <Sel label="Vacation pay policy" value={emp.vacation_pay_policy || ""}
                  onSave={save("vacation_pay_policy")} options={[]} placeholder="Select a policy" testid="profile-vacation-policy" />
                <Field label="Stock option" value={emp.stock_option || ""} onSave={save("stock_option")} testid="profile-stock-option" />
                <div className="col-span-2 md:col-span-4"><Field label="Additional terms" value={emp.additional_terms || ""} onSave={save("additional_terms")} testid="profile-additional-terms" /></div>
              </div>
              {isOwner && (
                <p className="mt-2 inline-block rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-amber-300" data-testid="profile-vacation-exempt-note">
                  Vacation pay exempt — owner role: never accrued or paid
                </p>
              )}
              {!isOwner && emp.vacation_opt_out && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <p className="inline-block rounded-md border border-sky-500/40 bg-sky-500/10 px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-sky-300" data-testid="profile-vacation-payout-note">
                    Vacation waiver on file — paid out on each pay cheque
                  </p>
                  <button onClick={viewWaiver} data-testid="view-waiver-btn"
                    className="rounded-md border border-white/20 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white/70 hover:border-white/40 hover:text-white">
                    View waiver
                  </button>
                </div>
              )}

              {/* Vacation bank change log — every admin baseline set is
                  recorded (who / when / value / pay week it applies from) */}
              {(emp.vacation_bank_history || []).length > 0 && (
                <div className="mt-3" data-testid="vac-bank-history">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-white/50">Vacation bank change log</p>
                  <div className="mt-1 space-y-0.5">
                    {(emp.vacation_bank_history || [])
                      .slice().sort((a, b) => (b.set_at || "").localeCompare(a.set_at || ""))
                      .map((h, i) => (
                        <p key={i} className="font-mono text-[10px] text-white/50" data-testid={`vac-bank-history-${i}`}>
                          ${Number(h.value).toFixed(2)} · applies from pay week of {h.week_start}
                          {h.set_at ? ` · set ${new Date(h.set_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}` : ""}
                          {h.set_by ? ` by ${h.set_by}` : ""}
                        </p>
                      ))}
                  </div>
                </div>
              )}

              {/* Imported Knit pay stubs — scanned PDFs whose amounts count
                  toward the employee's yearly pay stats (home tab). Labelled
                  by the pay date inside each PDF. */}
              <div className="mt-4" data-testid="imported-paystubs">
                <div className="flex items-center justify-between">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-white/50">Imported pay stubs</p>
                  <label className={`cursor-pointer rounded border border-white/15 bg-white/5 px-2 py-1 font-mono text-[9px] font-semibold uppercase tracking-widest text-white/70 hover:border-[#1a3fd1] hover:text-white ${importingStub ? "pointer-events-none opacity-50" : ""}`}
                    data-testid="import-paystub-btn">
                    {importingStub ? "Scanning…" : "+ Import PDF / CSV"}
                    <input type="file" accept="application/pdf,.pdf,text/csv,.csv" multiple className="hidden"
                      data-testid="import-paystub-file" onChange={importPaystub} disabled={importingStub} />
                  </label>
                </div>

                {/* Year check: gaps (weeks with NO stub) + zip export */}
                <div className="mt-2 flex flex-wrap items-center gap-2 rounded border border-white/10 bg-white/[0.03] px-2 py-1.5" data-testid="stub-year-check">
                  <div className="flex items-center gap-1 font-mono text-[10px] text-white/70">
                    <button type="button" onClick={() => setCheckYear((y) => y - 1)} aria-label="Previous year" data-testid="stub-year-prev"
                      className="rounded border border-white/15 px-1.5 py-0.5 hover:border-[#1a3fd1] hover:text-white">‹</button>
                    <span className="min-w-[3ch] text-center font-bold text-white" data-testid="stub-year-label">{checkYear}</span>
                    <button type="button" onClick={() => setCheckYear((y) => y + 1)} aria-label="Next year" data-testid="stub-year-next"
                      className="rounded border border-white/15 px-1.5 py-0.5 hover:border-[#1a3fd1] hover:text-white">›</button>
                  </div>
                  {yearCheck && (
                    <p className="font-mono text-[10px] text-white/60" data-testid="stub-year-summary">
                      {yearCheck.no_data
                        ? "no stubs on file this year"
                        : <>
                            {yearCheck.covered} of {yearCheck.expected} weeks on file
                            {yearCheck.blank_stubs ? ` · ${yearCheck.blank_stubs} blank` : ""}
                            {" · "}
                            {yearCheck.missing.length === 0
                              ? <span className="font-bold text-emerald-300">no gaps</span>
                              : <button type="button" onClick={() => setShowMissing((v) => !v)} data-testid="stub-year-missing-toggle"
                                  className="font-bold text-amber-700 underline-offset-2 hover:underline dark:text-[#fde68a]">
                                  {yearCheck.missing.length} week{yearCheck.missing.length === 1 ? "" : "s"} missing {showMissing ? "▴" : "▾"}
                                </button>}
                          </>}
                    </p>
                  )}
                  <button type="button" onClick={exportYearZip} disabled={exporting || !(yearCheck?.imported_stubs)}
                    data-testid="stub-year-export"
                    title={yearCheck?.imported_stubs ? `Download every imported ${checkYear} stub (originals) as one zip` : `No imported stubs for ${checkYear}`}
                    className="ml-auto rounded border border-white/15 bg-white/5 px-2 py-1 font-mono text-[9px] font-semibold uppercase tracking-widest text-white/70 hover:border-[#1a3fd1] hover:text-white disabled:cursor-not-allowed disabled:opacity-40">
                    {exporting ? "Zipping…" : `Download ${checkYear} zip`}
                  </button>
                  {showMissing && yearCheck?.missing?.length > 0 && (
                    <div className="w-full" data-testid="stub-year-missing-list">
                      <p className="font-mono text-[9px] uppercase tracking-widest text-white/45">Weeks with no stub (Mon start)</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {yearCheck.missing.map((m) => (
                          <span key={m} className="rounded bg-[#5a4208] px-1.5 py-0.5 font-mono text-[10px] text-[#fde68a]">{m}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {importedStubs.length === 0 ? (
                  <p className="mt-1 text-[10px] text-white/35">No imported stubs — upload pay-stub PDFs or CSV exports and their amounts join this employee&apos;s yearly pay stats.</p>
                ) : (
                  <div className="mt-1 space-y-0.5">
                    {importedStubs.map((s) => (
                      <p key={s.import_id} className="flex flex-wrap items-center gap-2 font-mono text-[10px] text-white/50" data-testid={`imported-paystub-${s.import_id}`}>
                        <button type="button" onClick={() => setViewImport(s.import_id)}
                          data-testid={`imported-paystub-open-${s.import_id}`}
                          title="Open as a readable pay-stub card (download the original from there)"
                          className="text-left text-white/70 underline-offset-2 hover:text-white hover:underline">
                          {s.label}
                        </button>
                        <span>
                          · gross ${Number(s.gross || 0).toFixed(2)} · net ${Number(s.net || 0).toFixed(2)}
                          {s.employee_name_on_stub ? ` · stub says ${s.employee_name_on_stub}` : ""}
                        </span>
                        {s.blank && (
                          <span className="rounded-full bg-[#5a4208] px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-[#fde68a]" data-testid={`imported-paystub-blank-${s.import_id}`}>Blank $0</span>
                        )}
                        {s.source === "knit_csv" && (
                          <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-white/70">CSV</span>
                        )}
                        <button type="button" onClick={() => deleteImportedStub(s)}
                          aria-label={`Remove ${s.label}`}
                          data-testid={`imported-paystub-delete-${s.import_id}`}
                          className="text-white/25 transition hover:text-red-400">✕</button>
                      </p>
                    ))}
                  </div>
                )}
              </div>

              <SectionTitle>Pay Rate</SectionTitle>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Sel label="Wage per" value={wagePer} onSave={async (v) => saveWage(v, wageAmt)} options={WAGE_PER_OPTIONS} placeholder={null} testid="profile-wage-per" />
                <Field key={`wage-${wageRefuseNonce}`} label={wagePer === "Hour" ? "Wage ($/hour)" : `Wage ($/${wagePer.toLowerCase()})`} value={wageAmt}
                  onSave={async (v) => saveWage(wagePer, v)} testid="profile-wage" mono />
                <Field label="Additional $ per hour" value={emp.wage_additional ?? ""} onSave={async (v) => save("wage_additional")(parseFloat(v) || 0)} testid="profile-wage-additional" mono />
                <Sel label="Overtime exempt" value={emp.overtime_exempt ? "Yes" : "No"}
                  onSave={(v) => {
                    const nb = v === "Yes";
                    if (nb === !!emp.overtime_exempt) return;
                    askVacEffective("overtime_exempt", nb, "Overtime exempt");
                  }}
                  options={YES_NO} placeholder={null} testid="profile-overtime-exempt" />
                <Sel label="Department" value={emp.cost_department || ""} onSave={save("cost_department")} options={DEPARTMENTS} placeholder="Please select" testid="profile-cost-department" />
                <Sel label="Location" value={emp.pay_rate_location || ""} onSave={save("pay_rate_location")} options={PAY_RATE_LOCATIONS} placeholder="Please select" testid="profile-pay-rate-location" />
                <Field label="Job code" value={emp.job_code || ""} onSave={save("job_code")} testid="profile-job-code" />
              </div>
              <p className="mt-2 text-[11px] font-semibold text-red-400">
                Adding, removing or editing this pay rate will result in the loss of any currently unconfirmed payroll data linked to this pay rate, including vacation pay.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <label htmlFor="wage-effective" className="font-mono text-[9px] uppercase tracking-widest text-white/50">
                  Rate change applies from (required when changing the rate)
                </label>
                <input id="wage-effective" type="date" value={wageEffective}
                  onChange={(e) => setWageEffective(e.target.value)}
                  data-testid="profile-wage-effective"
                  className="rounded-md border border-white/15 bg-white/5 px-2 py-1 font-mono text-xs text-white" />
                <span className="text-[10px] text-white/40">
                  The week containing this date and all later weeks use the new rate — earlier weeks keep the old one.
                </span>
              </div>
              <SectionTitle>Tax Options and Advanced Settings</SectionTitle>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Sel label="CPP exempt" value={tx.cpp_exempt || "No"} onSave={saveTax("cpp_exempt")} options={YES_NO} placeholder={null} testid="profile-cpp-exempt" />
                <Sel label="EI exempt" value={tx.ei_exempt || "No"} onSave={saveTax("ei_exempt")} options={YES_NO} placeholder={null} testid="profile-ei-exempt" />
                <Sel label="FED exempt" value={tx.fed_exempt || "No"} onSave={saveTax("fed_exempt")} options={YES_NO} placeholder={null} testid="profile-fed-exempt" />
                <Sel label="PROV exempt" value={tx.prov_exempt || "No"} onSave={saveTax("prov_exempt")} options={YES_NO} placeholder={null} testid="profile-prov-exempt" />
                <Sel label="WCB/WSIB exempt" value={tx.wcb_exempt || "No"} onSave={saveTax("wcb_exempt")} options={YES_NO} placeholder={null} testid="profile-wcb-exempt" />
                <Sel label="EHT exempt" value={tx.eht_exempt || "No"} onSave={saveTax("eht_exempt")} options={YES_NO} placeholder={null} testid="profile-eht-exempt" />
                <Sel label="Indian's Status tax exempt" value={tx.indian_status_exempt || "No"} onSave={saveTax("indian_status_exempt")} options={YES_NO} placeholder={null} testid="profile-indian-exempt" />
                <Sel label="Default option for T4 box 45" value={tx.t4_box45 || "Code 1 (Not eligible)"} onSave={saveTax("t4_box45")} options={T4_BOX45_CODES} placeholder={null} testid="profile-t4-box45" />
                <div className="col-span-2">
                  <Sel label="Federal TD1" value={tx.federal_td1 || "Code 1 ($0.00 to $16,452.00)"} onSave={saveTax("federal_td1")} options={FEDERAL_TD1_CODES} placeholder={null} testid="profile-federal-td1" />
                </div>
                <div className="col-span-2">
                  <Sel label="Provincial TD1" value={tx.provincial_td1 || "Code 1 ($0.00 to $12,989.00)"} onSave={saveTax("provincial_td1")} options={PROVINCIAL_TD1_CODES} placeholder={null} testid="profile-provincial-td1" />
                </div>
                <Field label="Additional tax withholding per pay period" value={tx.additional_tax ?? ""} onSave={saveTax("additional_tax")} placeholder="0.0" testid="profile-additional-tax" mono />
                <Field label="Other federal tax credits (K3)" value={tx.k3 ?? ""} onSave={saveTax("k3")} placeholder="0.0" testid="profile-k3" mono />
                <Field label="Other prov tax credits (K3P)" value={tx.k3p ?? ""} onSave={saveTax("k3p")} placeholder="0.0" testid="profile-k3p" mono />
              </div>
              <p className="mt-2 font-mono text-[9px] uppercase tracking-widest text-white/30">
                CPP/EI/FED/PROV exemptions, TD1 claim codes, K3/K3P credits and additional withholding are applied on the next payroll run.
              </p>

              <SectionTitle>Recurring Income</SectionTitle>
              <div className="space-y-1.5" data-testid="profile-recurring-income">
                {(emp.recurring_incomes || []).map((inc) => (
                  <div key={inc.income_id} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3" data-testid={`income-row-${inc.income_id}`}>
                    <p className="rounded-md border border-white/10 bg-white/[0.02] px-2 py-1.5 text-sm text-white/80">{inc.type}</p>
                    <p className="rounded-md border border-white/10 bg-white/[0.02] px-2 py-1.5 font-mono text-xs text-white/50">
                      {inc.amount} {["Regular Pay", "Overtime", "Time Worked on Holiday"].includes(inc.type) ? "hours" : "$"}
                    </p>
                    <p className="rounded-md border border-white/10 bg-white/[0.02] px-2 py-1.5 font-mono text-xs text-white/50">Defaulted to {inc.pay_rate || "Wage 1"}</p>
                    <button onClick={() => removeRecurringIncome(inc.income_id)} aria-label="Remove income" data-testid={`income-del-${inc.income_id}`}
                      className="rounded p-1.5 text-white/40 hover:bg-white/10 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                {!(emp.recurring_incomes || []).length && (
                  <p className="py-3 text-center text-xs text-white/30">You have no recurring income yet.</p>
                )}
              </div>
              <button onClick={() => setAddIncomeOpen(true)} data-testid="add-recurring-income-btn"
                className="mt-2 inline-flex items-center gap-1 rounded-md bg-amber-600 px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-widest text-white hover:bg-amber-500">
                <Plus className="h-3 w-3" /> Add Recurring Income
              </button>

              <SectionTitle>Deductions</SectionTitle>
              <div className="space-y-1.5" data-testid="profile-recurring-deductions">
                {(emp.recurring_deductions || []).map((d) => (
                  <div key={d.deduction_id} className="grid grid-cols-[1fr_auto_auto] items-center gap-3" data-testid={`deduction-row-${d.deduction_id}`}>
                    <p className="rounded-md border border-white/10 bg-white/[0.02] px-2 py-1.5 text-sm text-white/80">{d.type}</p>
                    <p className="rounded-md border border-white/10 bg-white/[0.02] px-2 py-1.5 font-mono text-xs text-white/50">
                      {d.pct ? `${d.pct}%/period ` : ""}{d.per_hour ? `$${d.per_hour}/h ` : ""}{d.annual_max ? `· max $${d.annual_max}/yr` : ""}
                    </p>
                    <button onClick={() => removeRecurringDeduction(d.deduction_id)} aria-label="Remove deduction" data-testid={`deduction-del-${d.deduction_id}`}
                      className="rounded p-1.5 text-white/40 hover:bg-white/10 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                {!(emp.recurring_deductions || []).length && (
                  <p className="py-3 text-center text-xs text-white/30">You have no deduction yet.</p>
                )}
              </div>
              <button onClick={() => setAddDeductionOpen(true)} data-testid="add-recurring-deduction-btn"
                className="mt-2 inline-flex items-center gap-1 rounded-md bg-amber-600 px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-widest text-white hover:bg-amber-500">
                <Plus className="h-3 w-3" /> Add Recurring Deduction
              </button>

              <SectionTitle>Historical Tax Options</SectionTitle>
              <p className="py-3 text-center text-xs text-white/30">You have no historical tax option records yet.</p>
              <SectionTitle>Historical Compensation</SectionTitle>
              {/* Dated pay changes — every tracked compensation change (rates,
                  OT-exempt, vacation rate/option) lands here with its effective
                  date. An entry applies from its date until the next later
                  change, or today when it's the latest. */}
              <div className="mt-1" data-testid="profile-rate-history">
                <div className="flex items-center justify-between">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-white/50">Dated pay changes</p>
                  <button type="button" onClick={() => setAddingRate((v) => !v)}
                    data-testid="rate-history-add-toggle"
                    className="rounded border border-white/15 bg-white/5 px-2 py-1 font-mono text-[9px] font-semibold uppercase tracking-widest text-white/70 hover:border-[#1a3fd1] hover:text-white">
                    {addingRate ? "Cancel" : "+ Add new"}
                  </button>
                </div>
                {(emp.rate_history || []).filter((h) => (h.effective_date || "") > "1971").length === 0 && !addingRate && (
                  <p className="py-3 text-center text-xs text-white/30">You have no historical compensation records yet.</p>
                )}
                <div className="mt-1 space-y-0.5">
                  {(emp.rate_history || []).filter((h) => (h.effective_date || "") > "1971")
                    .slice().sort((a, b) => (b.effective_date || "").localeCompare(a.effective_date || ""))
                    .map((h, i) => (
                    <p key={i} className="flex items-center gap-2 font-mono text-[10px] text-white/50">
                      <span>
                        {histEntryLabel(h)}
                        {" "}· from {h.effective_date}{h.changed_by ? ` · set by ${h.changed_by}` : ""}
                      </span>
                      <button type="button" onClick={() => deleteRateEntry(h)}
                        aria-label="Remove this rate entry"
                        data-testid={`rate-history-delete-${h.field}-${h.effective_date}`}
                        className="text-white/25 transition hover:text-red-400">✕</button>
                    </p>
                  ))}
                </div>
                {addingRate && (
                  <div className="mt-2 flex flex-wrap items-end gap-2 rounded-md border border-white/10 bg-white/[0.03] p-2" data-testid="rate-history-add-form">
                    <label className="block">
                      <span className="font-mono text-[9px] uppercase tracking-widest text-white/50">Wage per</span>
                      <select value={newRate.per} onChange={(e) => setNewRate((s) => ({ ...s, per: e.target.value }))}
                        data-testid="rate-history-add-per"
                        className="mt-0.5 block rounded border border-white/15 bg-white/5 px-2 py-1 font-mono text-xs text-white">
                        {["Hour", "Week", "Month", "Year"].map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className="font-mono text-[9px] uppercase tracking-widest text-white/50">Rate amount</span>
                      <input type="text" inputMode="decimal" value={newRate.amount}
                        onChange={(e) => setNewRate((s) => ({ ...s, amount: e.target.value.replace(/[^0-9.]/g, "") }))}
                        data-testid="rate-history-add-amount" placeholder="33.00"
                        className="mt-0.5 block w-24 rounded border border-white/15 bg-white/5 px-2 py-1 font-mono text-xs text-white" />
                    </label>
                    <label className="block">
                      <span className="font-mono text-[9px] uppercase tracking-widest text-white/50">Commenced</span>
                      <input type="date" value={newRate.date}
                        onChange={(e) => setNewRate((s) => ({ ...s, date: e.target.value }))}
                        data-testid="rate-history-add-date"
                        className="mt-0.5 block rounded border border-white/15 bg-white/5 px-2 py-1 font-mono text-xs text-white" />
                    </label>
                    <button type="button" onClick={addRateEntry} disabled={savingRate}
                      data-testid="rate-history-add-save"
                      className="rounded bg-[#1a3fd1] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1a3fd1]/85 disabled:opacity-50">
                      {savingRate ? "Saving…" : "Save entry"}
                    </button>
                  </div>
                )}
                <p className="mt-1 text-[10px] text-white/35">
                  Every pay change applies from its date until the next later change — or until today when it&apos;s the latest. The most recent entry sets the current value. Payroll runs weekly, so a mid-week date applies to that whole pay week.
                </p>
              </div>
            </div>
          )}

          {/* ============================ DIRECT DEPOSIT ============================ */}
          {tab === "DIRECT DEPOSIT" && (
            <div data-testid="profile-direct-deposit">
              <div className="flex items-start justify-between gap-2">
                <SectionTitle>Direct Deposit</SectionTitle>
                <ImportBtn section="deposit" />
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Sel label="Institution number" value={emp.bank_institution || ""} onSave={save("bank_institution")} options={BANKS} placeholder="Please select" testid="profile-bank-institution" />
                <Sel label="Account type" value={emp.bank_account_type || "Checking"} onSave={save("bank_account_type")} options={ACCOUNT_TYPES} placeholder={null} testid="profile-account-type" />
                <Field label="Transit number" value={emp.bank_transit || ""} onSave={save("bank_transit")} testid="profile-bank-transit" mono />
                <Field label="Account number" value={emp.bank_account || ""} onSave={save("bank_account")} testid="profile-bank-account" mono />
              </div>
              {(emp.bank_institution || emp.bank_transit || emp.bank_account) && (
                <button onClick={deleteBankAccount} data-testid="delete-bank-account"
                  className="mt-3 font-mono text-[10px] uppercase tracking-widest text-red-400 hover:text-red-300">
                  Delete bank account
                </button>
              )}
              <p className="mt-2 font-mono text-[9px] uppercase tracking-widest text-white/30">Account details are masked for non-owner roles and never echoed back into saves.</p>
            </div>
          )}

          {/* ============================ PTO ============================ */}
          {tab === "PTO" && (
            <div data-testid="profile-pto">
              <div className="mb-3 flex flex-wrap items-end gap-2">
                <label className="block">
                  <span className="mb-0.5 block font-mono text-[9px] uppercase tracking-widest text-white/40">Start date</span>
                  <input type="date" value={ptoFilters.from} onChange={(e) => setPtoFilters((f) => ({ ...f, from: e.target.value }))}
                    data-testid="pto-start-filter"
                    className="h-8 rounded-md border border-white/10 bg-white/[0.03] px-2 text-xs text-white/80 focus:border-[#1a3fd1] focus:outline-none" />
                </label>
                <label className="block">
                  <span className="mb-0.5 block font-mono text-[9px] uppercase tracking-widest text-white/40">End date</span>
                  <input type="date" value={ptoFilters.to} onChange={(e) => setPtoFilters((f) => ({ ...f, to: e.target.value }))}
                    data-testid="pto-end-filter"
                    className="h-8 rounded-md border border-white/10 bg-white/[0.03] px-2 text-xs text-white/80 focus:border-[#1a3fd1] focus:outline-none" />
                </label>
                <label className="block">
                  <span className="mb-0.5 block font-mono text-[9px] uppercase tracking-widest text-white/40">Status</span>
                  <select value={ptoFilters.status} onChange={(e) => setPtoFilters((f) => ({ ...f, status: e.target.value }))}
                    data-testid="pto-status-filter"
                    className="h-8 rounded-md border border-white/10 bg-white/[0.03] px-2 text-xs text-white/80">
                    <option value="">All Statuses</option>
                    {PTO_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <input value={ptoFilters.q} onChange={(e) => setPtoFilters((f) => ({ ...f, q: e.target.value }))}
                  placeholder="Search reason" data-testid="pto-search"
                  className="h-8 w-44 self-end rounded-md border border-white/10 bg-white/[0.03] px-2 text-xs text-white/80 focus:border-[#1a3fd1] focus:outline-none" />
              </div>
              <div className="overflow-hidden rounded-lg border border-white/10">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-widest text-white/40">
                      <th className="px-3 py-2">Dates</th>
                      <th className="px-3 py-2">Reason</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Requested</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTimeOff.map((r) => (
                      <tr key={r.request_id} className="border-b border-white/5 last:border-0" data-testid={`pto-row-${r.request_id}`}>
                        <td className="px-3 py-2 font-mono text-xs text-white/80">
                          {r.date_from}{r.date_to && r.date_to !== r.date_from ? ` → ${r.date_to}` : ""}{r.partial ? ` ${r.time_from}–${r.time_to}` : ""}
                        </td>
                        <td className="px-3 py-2 text-white/70">{r.reason}</td>
                        <td className="px-3 py-2">
                          <span className={`rounded-full border px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest ${
                            r.status === "approved" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                            : r.status === "denied" ? "border-red-500/40 bg-red-500/10 text-red-300"
                            : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-[10px] text-white/40">{(r.created_at || "").slice(0, 10)}</td>
                      </tr>
                    ))}
                    {!filteredTimeOff.length && (
                      <tr><td colSpan={4} className="px-3 py-8 text-center text-xs text-white/30">No time off requests found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ============================ DOCUMENTS ============================ */}
          {tab === "DOCUMENTS" && (
            <DocumentsTab employeeId={employeeId} onCreateRoe={() => setRoe("new")} onOpenRoe={(r) => setRoe(r)} />
          )}

          {/* ============================ PAYSTUBS ============================ */}
          {tab === "PAYSTUBS" && (
            <div data-testid="profile-paystubs">
              <div className="mb-2 flex items-center justify-end">
                <select value={stubYear} onChange={(e) => setStubYear(e.target.value)} data-testid="paystubs-year-filter"
                  className="h-8 rounded-md border border-white/10 bg-white/[0.03] px-2 text-xs text-white/80">
                  <option value="">All years</option>
                  {stubYears.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div className="overflow-hidden rounded-lg border border-white/10">
                <table className="w-full text-left text-sm" data-testid="paystubs-table">
                  <thead>
                    <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-widest text-white/40">
                      <th className="px-3 py-2">Pay date</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">Pay cycle</th>
                      <th className="px-3 py-2 text-right">Total</th>
                      <th className="px-3 py-2 text-right">Net</th>
                      <th className="px-3 py-2">Payment method</th>
                      <th className="px-3 py-2 text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownStubs.map((s) => {
                      const wkEnd = s.week_start
                        ? new Date(new Date(`${s.week_start}T12:00:00`).getTime() + 6 * 86400000).toISOString().slice(0, 10)
                        : "";
                      return (
                        <tr key={s.paystub_id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03]" data-testid={`profile-stub-row-${s.paystub_id}`}>
                          <td className="px-3 py-2 font-mono text-xs text-white/80">{(s.created_at || "").slice(0, 10)}</td>
                          <td className="px-3 py-2 text-white/70">Regular Cycle</td>
                          <td className="px-3 py-2 font-mono text-xs text-white/60">{s.week_start} – {wkEnd}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-white">{money(s.gross_pay_total ?? s.gross_pay)}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-emerald-300">{money(s.net_pay)}</td>
                          <td className="px-3 py-2 text-white/60">Cheque with tax</td>
                          <td className="px-3 py-2 text-right">
                            <button onClick={() => setViewStubId(s.paystub_id)} data-testid={`paystub-view-${s.paystub_id}`}
                              className="font-mono text-[10px] uppercase tracking-widest text-[#8fa4f0] hover:text-white">view</button>
                          </td>
                        </tr>
                      );
                    })}
                    {!shownStubs.length && (
                      <tr><td colSpan={7} className="px-3 py-8 text-center text-xs text-white/30">No paystubs yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          </>
          )}
        </div>
      </div>

      {/* modals */}
      <input ref={importInput} type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden"
        data-testid="import-file-input" onChange={onImportFile} />
      {dismissOpen && (
        <DismissEmployeeModal employee={emp} onClose={() => setDismissOpen(false)}
          onDismissed={() => { onChanged?.(); closeSelf(); }} />
      )}
      {addIncomeOpen && <AddRecurringIncomeModal onClose={() => setAddIncomeOpen(false)} onSave={addRecurringIncome} />}
      {addDeductionOpen && <AddRecurringDeductionModal onClose={() => setAddDeductionOpen(false)} onSave={addRecurringDeduction} />}
      {viewStubId && <KnitPaystubView paystubId={viewStubId} onClose={() => setViewStubId(null)} />}
      {viewImport && <ImportedStubCard importId={viewImport} onClose={() => setViewImport(null)} />}
      <StubImportProgress progress={importProgress} testId="profile-import-progress" />

      {/* Effective-date prompt for vacation pay rate / payment option
          changes — dated historical compensation, no default date. */}
      {vacEff && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" data-testid="vac-effective-dialog">
          <div className="w-full max-w-sm rounded-xl border border-white/10 bg-[#0d0f14] p-5 shadow-2xl">
            <h3 className="text-sm font-bold uppercase tracking-widest text-white">When does this change apply?</h3>
            <p className="mt-2 text-xs leading-relaxed text-white/60">
              You changed the <span className="font-semibold text-white">{vacEff.label}</span>.
              Pick the date it takes effect — earlier weeks and pay periods keep the old
              setting. The change is saved to the historical compensation log.
            </p>
            <label className="mt-4 block">
              <span className="mb-0.5 block font-mono text-[9px] uppercase tracking-widest text-white/40">Applies from</span>
              <input type="date" value={vacEffDate} onChange={(e) => setVacEffDate(e.target.value)}
                data-testid="vac-effective-date-input"
                className="h-8 w-full rounded-md border border-white/10 bg-white/[0.03] px-2 font-mono text-sm text-white/90 focus:border-[#1a3fd1] focus:outline-none" />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={cancelVacEffective} data-testid="vac-effective-cancel"
                className="rounded-md border border-white/20 px-3 py-1.5 text-xs font-semibold text-white/70 hover:border-white/40 hover:text-white">
                Cancel
              </button>
              <button type="button" onClick={confirmVacEffective} disabled={vacEffBusy} data-testid="vac-effective-save"
                className="rounded-md bg-[#1a3fd1] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1a3fd1]/85 disabled:opacity-50">
                {vacEffBusy ? "Saving…" : "Save change"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
